// src/pages/tariff/TariffCreatePage.js - Fixed to include company selection
import React from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Snackbar,
  Alert,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { createTariff } from '../../api/tariffAPI';
import { getCompanies } from '../../api/companyAPI';
import TariffForm from '../../components/tariff/TariffForm';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const TariffCreatePage = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const queryClient = useQueryClient();
  const [selectedCompanyId, setSelectedCompanyId] = React.useState(companyId || '');
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch companies for dropdown
  const { data: companies, isLoading: isLoadingCompanies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getCompanies()
  });

  // Set default company if coming from URL or use first company
  React.useEffect(() => {
    if (companies && companies.length > 0 && !selectedCompanyId) {
      if (companyId) {
        setSelectedCompanyId(companyId);
      } else {
        setSelectedCompanyId(companies[0].CompanyId.toString());
      }
    }
  }, [companies, companyId, selectedCompanyId]);

  const createMutation = useMutation({
    mutationFn: createTariff,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tariffs'] });
      queryClient.invalidateQueries({ queryKey: ['company-tariffs'] });
      
      setSnackbar({
        open: true,
        message: 'Tariff created successfully',
        severity: 'success'
      });
      
      // Navigate to the created tariff's details page
      setTimeout(() => {
        navigate(`/tariffs/${data.TariffsId}`);
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error creating tariff: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
  };

  const handleSubmit = async (formData) => {
    if (!selectedCompanyId) {
      setSnackbar({
        open: true,
        message: 'Please select a company',
        severity: 'error'
      });
      return;
    }

    try {
      const dataWithCompany = {
        ...formData,
        TariffsCompanyId: parseInt(selectedCompanyId)
      };
      await createMutation.mutateAsync(dataWithCompany);
    } catch (err) {
      // Error is handled by the mutation
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Show loading spinner while fetching companies
  if (isLoadingCompanies) {
    return <LoadingSpinner />;
  }

  // Show error if no companies available
  if (!companies || companies.length === 0) {
    return <ErrorAlert message="No companies available. Please create a company first." />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          {/* <Breadcrumbs aria-label="breadcrumb" mb={1}>
            <Link component={RouterLink} to="/" color="inherit">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/tariffs" color="inherit">
              Tariffs
            </Link>
            <Typography color="textPrimary">Create Tariff</Typography>
          </Breadcrumbs> */}
          <Typography variant="h4">Create New Tariff</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/tariffs" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Tariffs
        </Button>
      </Box>

      {/* Company Selection */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Select Company</Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="company-select-label">Company</InputLabel>
          <Select
            labelId="company-select-label"
            value={selectedCompanyId}
            label="Company"
            onChange={handleCompanyChange}
            error={createMutation.isError && !selectedCompanyId}
          >
            <MenuItem value="" disabled>
              <em>Select a company</em>
            </MenuItem>
            {companies.map((company) => (
              <MenuItem key={company.CompanyId} value={company.CompanyId.toString()}>
                {company.CompanyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Show tariff form only if a company is selected */}
      {selectedCompanyId && (
        <>
          {createMutation.isError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {createMutation.error.message}
            </Alert>
          )}
          
          <TariffForm 
            tariff={null}
            companyId={parseInt(selectedCompanyId)}
            onSubmit={handleSubmit}
            isLoading={createMutation.isPending}
          />
        </>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TariffCreatePage;